import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageSquare, Clock, Send, Headphones } from "lucide-react";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-950 text-white relative overflow-hidden">
      {/* Animated Gradient Orbs */}
      <div className="absolute -top-20 -left-20 w-72 h-72 bg-pink-600/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-rose-600/30 rounded-full blur-3xl animate-ping"></div>

      <div className="relative max-w-6xl mx-auto p-8 space-y-8 z-10">

        {/* Header */}
        <div className="text-center">
          <h1 className="text-4xl font-extrabold flex items-center justify-center gap-3 mb-4">
            <MessageSquare className="w-10 h-10 text-pink-500 animate-bounce" />
            Contact Us
          </h1>
          <p className="text-gray-400 text-lg">Get in touch with the Sama Music team</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">

          {/* Contact Information */}
          <div className="space-y-6">

            <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-6 hover:shadow-pink-500/30 transition-all">
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <Headphones className="w-6 h-6 text-pink-400" /> Get In Touch
              </h2>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center">
                    <Mail className="w-6 h-6 text-pink-400" />
                  </div>
                  <div>
                    <p className="font-medium">Email Support</p>
                    <p className="text-gray-400">samasong.music@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center">
                    <Phone className="w-6 h-6 text-pink-400" />
                  </div>
                  <div>
                    <p className="font-medium">Phone Support</p>
                    <p className="text-gray-400">+91 8800340165</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-pink-400" />
                  </div>
                  <div>
                    <p className="font-medium">Office Address</p>
                    <p className="text-gray-400">old market, Tawang, Arunachal Pradesh, 790104</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-4 hover:shadow-rose-500/30 transition-all">
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <Clock className="w-6 h-6 text-pink-400" /> Support Hours
              </h2>

              <div className="space-y-3 text-gray-300">
                <div className="flex justify-between">
                  <span>Monday - Friday</span>
                  <span className="text-pink-400">9:00 AM - 6:00 PM EST</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday</span>
                  <span className="text-pink-400">10:00 AM - 4:00 PM EST</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span className="text-gray-500">Closed</span>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-4 hover:shadow-pink-500/30 transition-all">
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-pink-400" /> Response Times
              </h2>

              <div className="space-y-3 text-gray-300">
                <div className="flex justify-between">
                  <span>Email Support</span>
                  <span className="text-green-400">Within 24 hours</span>
                </div>
                <div className="flex justify-between">
                  <span>Phone Support</span>
                  <span className="text-green-400">Immediate</span>
                </div>
                <div className="flex justify-between">
                  <span>Emergency Issues</span>
                  <span className="text-yellow-400">Within 2 hours</span>
                </div>
              </div>
            </div>

          </div>

          {/* Contact Form */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl shadow-lg border border-white/10 p-6 space-y-6 hover:shadow-pink-500/30 transition-all">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Send className="w-6 h-6 text-pink-400" /> Send us a Message
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="your@email.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="What's this about?"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={6}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                  placeholder="Tell us how we can help you..."
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3 bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 rounded-lg font-medium transition-all hover:scale-105 shadow-lg shadow-pink-500/30"
              >
                Send Message
              </button>
            </form>
          </div>

        </div>

      </div>
    </div>
  );
}